/**
 * LPP-22290 / Requirement 5.1 — mutua esclusione UI delle coperture SC/SCDW/STP.
 * LPP-21950 — supporto Pack: getEffectiveSubtypes esplode packAncillaries per verificare
 *             le coperture contenute nei figli.
 *
 * Modulo condiviso tra stream/booking e stream/network.
 * Logica pura, senza dipendenze da React.
 */

const MUTUALLY_EXCLUSIVE_COVERAGES: ReadonlyArray<string> = [
	"SUPER_COVER",
	"SCDW",
	"STP",
];

/** @returns true se il subtype e' una copertura mutuamente esclusiva (SC/SCDW/STP). */
export const isMutuallyExclusiveCoverage = (subtype?: string | null): boolean => {
	if (!subtype) {
		return false;
	}
	const normalized = subtype.trim().toUpperCase();
	return MUTUALLY_EXCLUSIVE_COVERAGES.includes(normalized);
};

/**
 * Individua la copertura attiva tra i subtype selezionati sul RA. Data la mutua esclusione al piu'
 * una e' presente; ritorna la prima trovata o null.
 */
export const getActiveCoverage = (selectedSubtypes: Array<string | null | undefined>): string | null => {
	const found = (selectedSubtypes ?? []).find((s) => isMutuallyExclusiveCoverage(s));
	return found ? found!.trim().toUpperCase() : null;
};

/**
 * Stabilisce se una copertura candidata deve essere disabilitata in UI: e' una copertura e sul RA
 * ne e' gia' selezionata un'altra (diversa). La copertura gia' selezionata resta abilitata (per
 * poterla deselezionare). I non-coperture non sono mai disabilitati da questa regola.
 *
 * @param candidateSubtype  subtype dell'elemento in lista
 * @param selectedSubtypes  subtype attualmente selezionati sul RA
 */
export const isCoverageDisabledByMutualExclusion = (
	candidateSubtype: string | null | undefined,
	selectedSubtypes: Array<string | null | undefined>
): boolean => {
	if (!isMutuallyExclusiveCoverage(candidateSubtype)) {
		return false;
	}
	const active = getActiveCoverage(selectedSubtypes);
	if (!active) {
		return false;
	}
	return active !== candidateSubtype!.trim().toUpperCase();
};

/**
 * Restituisce tutti i subtype effettivi di un'ancillary, incluse le figlie del Pack.
 * Per un'ancillary non-Pack, restituisce [subtype].
 * Per un Pack, restituisce [subtype_padre, ...subtype_figlie].
 *
 * @param ancillary oggetto con almeno subtype (o ancillarySubType) e packAncillaries opzionale
 */
export const getEffectiveSubtypes = (ancillary: {
	subtype?: string | null;
	ancillarySubType?: string | null;
	packAncillaries?: Array<{ subtype?: string | null; ancillarySubType?: string | null }> | null;
}): string[] => {
	const direct = (ancillary.subtype ?? ancillary.ancillarySubType ?? "").trim().toUpperCase();
	const children = (ancillary.packAncillaries ?? [])
		.map((p) => (p.subtype ?? p.ancillarySubType ?? "").trim().toUpperCase())
		.filter(Boolean);
	return [direct, ...children].filter(Boolean);
};

/**
 * Verifica se un'ancillary (o le sue figlie Pack) contiene un determinato subtype.
 */
export const hasEffectiveSubtype = (
	ancillary: { subtype?: string | null; ancillarySubType?: string | null; packAncillaries?: Array<{ subtype?: string | null; ancillarySubType?: string | null }> | null },
	subtype: string
): boolean => getEffectiveSubtypes(ancillary).includes(subtype.trim().toUpperCase());
